<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { useCommonComposable } from '@/composables/commonComposable';
const { isMobile } = useCommonComposable();

const props = defineProps({
    keys: {
        type: Array,
        required: true
    },

    description: {
        type: String,
        required: false
    },

    position: {
        type: String,
        validator: value => ['absolute', 'relative'].indexOf(value) > -1,
        default: 'absolute'
    },
    isTyping: {
        type: Boolean,
        default: false
    },
    isMini: {
        type: Boolean,
        default: false
    }
});

const { useTypingAnimation } = useCommonComposable();
const { typedText } = useTypingAnimation(
    () => props.description,
    {
        typingSpeed: 125,
        restartDelay: 5000
    }
);

const isMac = computed(() => {
    if (typeof navigator === 'undefined') {
        return false;
    }

    return /(Mac|iPhone|iPad|iPod)/i.test(navigator.userAgent);
});

const macKeyMap = {
    ctrl: '⌘',
    control: '⌘',
    cmd: '⌘',
    command: '⌘',

    alt: '⌥',
    option: '⌥',

    shift: '⇧',

    enter: '↩',
    return: '↩',

    backspace: '⌫',
    delete: '⌦',

    esc: '⎋',
    escape: '⎋',

    tab: '⇥',

    space: '␣'
};

const formatKey = (key) => {
    if (!isMac.value) {
        return key;
    }

    let result = key;

    Object.entries(macKeyMap).forEach(([source, target]) => {
        result = result.replace(
            new RegExp(`\\b${source}\\b`, 'gi'),
            target
        );
    });

    return result;
};

const displayKeys = computed(() =>
    props.keys.map(formatKey)
);

const displayDescription = computed(() =>
    props.isTyping
        ? typedText.value
        : props.description
);
</script>

<template>
    <template v-if="!isMobile">
    <div v-if="!isMini" class="keyboard-shortcut" :style="[`position: ${position}`, description ? 'padding: 4px 6px 4px 4px' : 'padding: 4px']">
        <div class="keyboard-shortcut__keys">
            <template
                v-for="(key, index) in displayKeys"
                :key="key"
            >
                <kbd>{{ key }}</kbd>
            </template>
        </div>

        <span v-if="description" class="keyboard-shortcut__description">
            {{ displayDescription }}
        </span>
    </div>
    <div v-else class="keyboard-shortcut keyboard-shortcut__mini">
        <template
            v-for="(key, index) in displayKeys"
            :key="key"
        >
            <kbd>{{ key }}</kbd>
        </template>
    </div>
        </template>
</template>

<style scoped lang="scss">
@use '~@/assets/styles/vars' as *;
.keyboard-shortcut {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;
    background: $silver;
    box-sizing: border-box;
    border-radius: 10px;
    border: 1px solid $gray;
    cursor: help;

    background: linear-gradient(
            120deg,
            #f1f1f5 0%,
            #F8F8FF 20%,
            #F3F3F9 50%,
            #F8F8FF 80%,
            #f1f1f5 100%
    );

    background-size: 250% 250%;
    animation: shortcutGradient 10s ease infinite;
}

@keyframes shortcutGradient {
    0% {
        background-position: 0 50%;
    }

    50% {
        background-position: 100% 50%;
    }

    100% {
        background-position: 0 50%;
    }
}

.keyboard-shortcut__keys {
    display: flex;
    align-items: center;
    gap: 1px;
}

.keyboard-shortcut__separator {
    color: #888;
}

kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    white-space: nowrap;

    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    box-sizing: border-box;

    border: 1px solid $gray;
    border-radius: 6px;

    background: rgba(255, 255, 255, 0.6);

    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    user-select: none;

    letter-spacing: -2px;
    padding-right: 6px;
}

.keyboard-shortcut__mini {
    padding: 0 2px;
    border-radius: 5px;
    background: linear-gradient(
            120deg,
            #f1f1f5 0%,
            #F8F8FF 20%,
            #F3F3F9 50%,
            #F8F8FF 80%,
            #f1f1f5 100%
    );

    background-size: 250% 250%;
    animation: shortcutGradient 10s ease infinite;
    border: 0.5px solid $hard-gray;
    max-height: 17px;
}

.keyboard-shortcut__mini kbd {
    background: none;
    color: $hard-gray;
    border: none;
    font-size: 10px;
    line-height: 1;
}

.keyboard-shortcut__description {
    color: $hard-gray;
    font-size: 13px;
}
</style>